import { useEffect, useState, useMemo, useRef } from "react";
import { io } from "socket.io-client";

import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import QuizIcon from "@mui/icons-material/Quiz";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";

import api from "../../utils/api";

/* ===================== UI HELPERS ===================== */

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      elevation={0}
      variant="outlined"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "primary.light",
            color: "primary.main",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>

      <Box sx={{ mt: 1 }}>
        <Typography variant="h5" fontWeight={800}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function formatDateTime(dt) {
  if (!dt) return "-";
  return new Date(dt).toLocaleString();
}

/* ===================== MAIN COMPONENT ===================== */

export default function AdminDashboard() {
  const socketRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const notify = (msg, severity = "info") =>
    setToast({ open: true, msg, severity });

  /* ===================== LOAD DASHBOARD ===================== */

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/dashboard");
      setData(res.data);
    } catch (e) {
      const msg =
        e?.response?.data?.message || "Failed to load admin dashboard.";
      notify(msg, "error");
    } finally {
      setBusy(false);
    }
  };

  /* ===================== SOCKET ===================== */

  useEffect(() => {
    const token = localStorage.getItem("token");
    const sessionToken = localStorage.getItem("sessionToken");
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!token || !sessionToken) return;
    if (user.role !== "admin") return;

    socketRef.current = io(
      import.meta.env.VITE_API_URL || "https://api.cbt-master.com.ng",
      {
        autoConnect: false,
        auth: { token, sessionToken },
      }
    );

    socketRef.current.on("live-exams:update", load);
    socketRef.current.on("admin:sessions:update", load);

    socketRef.current.connect();

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    load();
  }, []);

  /* ===================== DATA ===================== */

  const summary = data?.summary || {
    students: 0,
    subjects: 0,
    exams: 0,
    questions: 0,
  };

  const recentExams = data?.recentExams || [];
  const topStudents = data?.topStudents || [];
  const activeStudents = data?.activeStudents || [];
  const subjectPopularity = data?.subjectPopularity || [];

  const maxActive = useMemo(
    () =>
      activeStudents.length
        ? Math.max(...activeStudents.map((d) => Number(d.count || 0)))
        : 1,
    [activeStudents]
  );

  const maxSubjectAttempts = useMemo(
    () =>
      subjectPopularity.length
        ? Math.max(...subjectPopularity.map((s) => Number(s.attempts || 0)))
        : 1,
    [subjectPopularity]
  );

  /* ===================== UI ===================== */

  return (
    <Box>
      {/* HEADER */}
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1}
        alignItems={isMobile ? "flex-start" : "center"}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight={800}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            High-level overview of students, subjects, exams, and activity.
          </Typography>
        </Box>
        {busy && <CircularProgress size={24} />}
      </Stack>

      {/* STATS */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[PeopleAltIcon, MenuBookIcon, QuizIcon, HelpOutlineIcon].map(
          (Icon, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}>
              <StatCard
                icon={Icon}
                label={
                  ["Total Students", "Subjects", "Exams Taken", "Questions"][i]
                }
                value={[
                  summary.students,
                  summary.subjects,
                  summary.exams,
                  summary.questions,
                ][i]}
              />
            </Grid>
          )
        )}
      </Grid>

      {/* CONTENT */}
      <Grid container spacing={3}>
        {/* LEFT */}
        <Grid item xs={12} md={7}>
          {[recentExams, topStudents].map((block, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{ borderRadius: 3, mb: 3, p: 2.5, overflowX: "auto" }}
            >
              {idx === 0 ? (
                <Typography fontWeight={700}>Recent Exams</Typography>
              ) : (
                <Typography fontWeight={700}>Top Students</Typography>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {idx === 0 ? (
                      <>
                        <TableCell>Student</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Submitted</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Student</TableCell>
                        <TableCell>Avg. Score</TableCell>
                        <TableCell>Attempts</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {block.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        {row.student_name || row.full_name}
                      </TableCell>
                      {idx === 0 && (
                        <>
                          <TableCell>{row.subject_name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${row.score}%`}
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDateTime(row.submitted_at)}
                          </TableCell>
                        </>
                      )}
                      {idx === 1 && (
                        <>
                          <TableCell>
                            <Chip
                              size="small"
                              label={`${row.avg_score}%`}
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{row.attempts}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </Grid>

        {/* RIGHT */}
        <Grid item xs={12} md={5}>
          {[activeStudents, subjectPopularity].map((list, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{ borderRadius: 3, p: 2.5, mb: 3 }}
            >
              <Typography fontWeight={700}>
                {idx === 0 ? "Daily Active Students" : "Subject Popularity"}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={1.2}>
                {list.map((d, i) => {
                  const pct =
                    ((Number(d.count || d.attempts) || 0) /
                      (idx === 0 ? maxActive : maxSubjectAttempts)) *
                    100;
                  return (
                    <Box key={i}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography variant="caption">
                          {d.day
                            ? new Date(d.day).toLocaleDateString()
                            : d.name}
                        </Typography>
                        <Typography variant="caption">
                          {d.count || d.attempts}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 6, borderRadius: 999 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          ))}
        </Grid>
      </Grid>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
